import React, {forwardRef, useCallback, useEffect, useState} from "react";
import Api from "../../../../../../../core/services/api/api";
import {UploadStates, WarrantyAndReturnMessageTypes} from "../../../../../../../core/constants/enums";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import WarrantyAndReturnSingleVendorTabChat from "./chat";
import PartSupportVendorSelection from "../../../../../../components/app-specific/part-support/part-support-vendor-selection";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useParams} from "react-router-dom";
import {ChatMessageOperationTypes} from "../index";

const WarrantyAndReturnSingleVendorTab = forwardRef(({
                                                         makes,
                                                         models,
                                                         loading,
                                                         height,
                                                         request,
                                                         statuses,
                                                         setRequest,
                                                         scrollInCaseOfChatChange,
                                                         setRequestProperty,
                                                     }, ref) => {
    const operatorAccessLevels = useAccessLevels();
    const params = useParams();
    const [chats, setChats] = useState([]);
    const [loadingChats, setLoadingChats] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Fetches the vendor chat information from the server and if the result of the api is successful, sets the
     * request info.
     * @type {function(): Promise<void>}
     */
    const getVendorChatMessages = useCallback(async () => {
        setLoadingChats(true);
        const response = await Api.getWarrantyAndReturnMessagesForVendor(params.requestId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setChats(response?.data?.messagesList
                ?.map(e => e.msgType?.id === WarrantyAndReturnMessageTypes.image
                    ? ({
                        ...e,
                        _reactKey: `_${createUUId()}`,
                        uploadState: {state: UploadStates.done},
                    })
                    : ({
                        ...e,
                        _reactKey: `_${createUUId()}`,
                        uploadState: {state: UploadStates.done}
                    })
                ));
            if (response?.data?.messagesList?.length) {
                setTimeout(() => {
                    if (!isMounted()) return;
                    scrollInCaseOfChatChange(response.data.messagesList[response.data.messagesList.length - 1], ChatMessageOperationTypes.add);
                }, 600)
            }
        }
        setLoadingChats(false);
    }, [isMounted, params.requestId, scrollInCaseOfChatChange]);

    /**
     * Listens for the changes in requestId of url and the request and with each change:
     * Fetches the chats for the vendors of the request if and only if the request has an assigned vendor to it.
     */
    useEffect(() => {
        if (!request?.vendor) return;
        void getVendorChatMessages();
    }, [getVendorChatMessages, request?.vendor]);

    /**
     * Sets the vendor of the request.
     * @type {function(*): void}
     */
    const onVendorSelected = useCallback((data) => {
        setRequest(prevState => ({
            ...prevState,
            vendor: data.vendorInfo,
            vendorAccessLink: data.vendorAccessLink,
        }))
    }, [setRequest]);

    return (
        <>
            {
                loadingChats || loading || !!request?.vendor
                    ? (
                        <WarrantyAndReturnSingleVendorTabChat
                            models={models}
                            makes={makes}
                            loading={loadingChats || loading}
                            ref={ref}
                            chats={chats}
                            setChats={setChats}
                            request={request}
                            scrollInCaseOfChatChange={scrollInCaseOfChatChange}
                            setRequest={setRequest}
                            height={height}
                            statuses={statuses}
                            setRequestProperty={setRequestProperty}
                        />
                    )
                    : operatorAccessLevels.packages.warrantyAndReturn.manageVendor &&
                    <PartSupportVendorSelection
                        request={request}
                        onSelect={onVendorSelected}
                    />
            }
        </>
    );
});

export default WarrantyAndReturnSingleVendorTab;
